<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import Button from '@spectee/components/Atoms/Button/index.vue';

defineExpose({
  name: 'SelectedSettings',
});

interface Props {
  labelName: string;
  selectedNum?: number;
  allNum?: number;
  isTableChecked?: boolean;
  isAllSelected?: boolean;
  itemsPerPage?: number;
  onDeleteButtonClick?: () => void;
}

const props = withDefaults(defineProps<Props>(), {
  allNum: 0,
  selectedNum: 0,
  itemsPerPage: 100,
});

const emit = defineEmits(['update:isAllSelected']);

const isTableChecked = ref(!!props.isTableChecked);
const isAllSelected = ref(!!props.isAllSelected);

watch(
  () => props.isTableChecked,
  newValue => {
    isTableChecked.value = !!newValue;
  }
);

watch(
  () => props.isAllSelected,
  newValue => {
    isAllSelected.value = !!newValue;
  }
);

// Auto-set isAllSelected=true when all items are selected
watch(
  () => [props.selectedNum, props.allNum] as const,
  ([sel, total]) => {
    const selected = sel ?? 0;
    const all = total ?? 0;
    const nextAll = all > 0 && selected >= all;
    if (nextAll !== isAllSelected.value) {
      isAllSelected.value = nextAll;
      if (nextAll) emit('update:isAllSelected', nextAll);
    }
  },
  { immediate: true }
);

// Show "選択解除" only when global-all is active
const shouldShowDeselect = computed(() => isAllSelected.value);

const toggleSelected = (nextAll: boolean) => {
  if (nextAll !== isAllSelected.value) {
    isAllSelected.value = nextAll;
    emit('update:isAllSelected', nextAll);
  }
};

const handleDeleteButtonClick = () => {
  if (props.onDeleteButtonClick) {
    props.onDeleteButtonClick();
  }
};
</script>

<template>
  <div class="SelectedSettings">
    <div class="SelectedSettings__state">
      <span class="SelectedSettings__selected">
        {{ selectedNum }} 件を選択中
      </span>
      <button
        v-if="isTableChecked || isAllSelected"
        class="SelectedSettings__selectButton"
        @click="toggleSelected(!isAllSelected)"
      >
        <template v-if="shouldShowDeselect"> 選択解除 </template>
        <template v-else> 該当する {{ allNum }} 件をすべて選択 </template>
      </button>
    </div>
    <Button
      class="SelectedSettings__deleteButton"
      tag="button"
      :label="labelName"
      dynamics="danger"
      @click="handleDeleteButtonClick"
    />
  </div>
</template>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_forward.scss' as *;

.SelectedSettings {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $service-unit * 2 $service-unit * 3;
  background-color: $color-gray-10;
  border: 1px solid $color-gray-30;
  border-radius: $service-border-radius;

  &__state {
    display: flex;
    column-gap: $service-unit * 4;
    align-items: center;
  }

  &__selected {
    font-size: $service-font-size-mp;
  }

  &__selectButton {
    font-size: $service-font-size-pp;
    color: $color-blue-60;
    cursor: pointer;
    background-color: transparent;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    row-gap: $service-unit * 2;

    &__state {
      flex-direction: column;
    }
  }
}
</style>
